/**
 * @file: useRefreshProfileOnForeground.ts
 * @copyright: (c) 2019-2020 sichuan zhichetech co., ltd.
 */

import { useAuthContext } from '@euler/app/flows/auth/context';
import { useServiceFactory } from '@euler/services/factory';
import { useEffect } from 'react';
import { AppState } from 'react-native';
import { withUserProfile } from './withUserProfile';

/**
 * Re-read the profile on launch and whenever the app comes back to the
 * foreground, so a trial that ended or a feature switched on or off in the
 * meantime shows without signing in again. Launch counts too: it restores
 * the profile saved at sign-in, and a store's switched-off feature stayed
 * on screen until the app happened to go to the background once.
 */
export function useRefreshProfileOnForeground(): void {
  const { authState$ } = useAuthContext();
  const { userService } = useServiceFactory();
  useEffect(() => {
    const refresh = () => {
      if (!authState$.value.isAuthenticated) return;
      userService.getUserProfileInfo().then(
        profile => authState$.next(withUserProfile(authState$.value, profile)),
        (e: unknown) => console.error('[profile] refresh failed', e),
      );
    };
    refresh();
    const sub = AppState.addEventListener('change', state => {
      if (state === 'active') refresh();
    });
    return () => sub.remove();
  }, [authState$, userService]);
}
